import { useState, useEffect } from 'react';
import { useUser } from '../context/UserContext';
import { getBolsaDocumentos, getBolsaDocumentosSelect, editarEstadoBolsaDocumentos } from '../api/apiConfig';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import { MdReceipt } from 'react-icons/md';
import { FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import { IoSearchOutline, IoClose } from 'react-icons/io5';
import * as XLSX from 'xlsx';

const BolsaDocumentos = () => {
  const { user } = useUser();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [searchTerm, setSearchTerm] = useState('');
  const itemsPerPage = 15;
  const [estados, setEstados] = useState([]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const [year, month, day] = dateString.split('-');
    return `${day}-${month}-${year}`;
  };

  const filterData = (dataToFilter) => {
    if (!searchTerm.trim()) return dataToFilter;
    const searchLower = searchTerm.toLowerCase().trim();
    return dataToFilter.filter(item => {
      const searchableFields = [item.RUT_CLIENTE, item.NOMBRE_CLIENTE, item.EJECUTIVO, item.RUT_DEUDOR, item.NOMBRE_DEUDOR, item.CLASIFICACION_DEUDOR, item.TIPO_DOCTO, item.FOLIO, item.FECHA_DOCTO, item.FECHA_RECLAMO, item.ESTADO, item.MONTO_TOTAL?.toString(), item.MONTO_NOTA_CREDITO?.toString()];
      return searchableFields.some(field => field != null && String(field).toLowerCase().includes(searchLower));
    });
  };

  const getSortedData = () => {
    let dataToSort = filterData(data);
    if (!sortColumn) return dataToSort;
    return [...dataToSort].sort((a, b) => {
      let aValue = a[sortColumn]; let bValue = b[sortColumn];
      if (aValue == null) aValue = ''; if (bValue == null) bValue = '';
      const aNum = typeof aValue === 'number' ? aValue : parseFloat(aValue);
      const bNum = typeof bValue === 'number' ? bValue : parseFloat(bValue);
      const isNumeric = !isNaN(aNum) && !isNaN(bNum) && isFinite(aNum) && isFinite(bNum);
      const comparison = isNumeric ? aNum - bNum : String(aValue).toLowerCase().localeCompare(String(bValue).toLowerCase(), 'es', { numeric: true, sensitivity: 'base' });
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  };

  const sortedData = getSortedData();
  const totalPages = Math.ceil(sortedData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = sortedData.slice(startIndex, endIndex);

  const handleSort = (column) => {
    if (sortColumn === column) setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    else { setSortColumn(column); setSortDirection('asc'); }
    setCurrentPage(1);
  };

  const handleSearchChange = (e) => { setSearchTerm(e.target.value); setCurrentPage(1); };
  const clearSearch = () => { setSearchTerm(''); setCurrentPage(1); };

  const getSortIcon = (column) => {
    if (sortColumn !== column) return <FaSort style={{ marginLeft: '4px', opacity: 0.3 }} />;
    return sortDirection === 'asc' ? <FaSortUp style={{ marginLeft: '4px' }} /> : <FaSortDown style={{ marginLeft: '4px' }} />;
  };

  const exportToExcel = () => {
    const dataToExport = getSortedData();
    const exportData = dataToExport.map(item => ({
      'Ejecutivo': item.EJECUTIVO,
      'RUT Cliente': item.RUT_CLIENTE,
      'Cliente': item.NOMBRE_CLIENTE,
      'RUT Deudor': item.RUT_DEUDOR,
      'Deudor': item.NOMBRE_DEUDOR,
      'Clasificación': item.CLASIFICACION_DEUDOR,
      'Tipo Doc': item.TIPO_DOCTO,
      'Folio': item.FOLIO,
      'Fecha Doc': formatDate(item.FECHA_DOCTO),
      'Monto Total': item.MONTO_TOTAL?.toFixed(0).replace('.', ',') || '0',
      'Fecha Reclamo': formatDate(item.FECHA_RECLAMO),
      'Monto Nota Crédito': item.MONTO_NOTA_CREDITO ? parseFloat(item.MONTO_NOTA_CREDITO).toFixed(0).replace('.', ',') : '',
      'Estado': item.ESTADO
    }));
    
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Bolsa de Documentos');
    
    XLSX.writeFile(wb, `bolsa_documentos_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const handleEstadoChange = async (id, nuevoEstado) => {
    try {
      await editarEstadoBolsaDocumentos(id, nuevoEstado);
      // Actualizar el estado local
      setData(prevData => 
        prevData.map(item => 
          item.ID === id ? { ...item, ESTADO: nuevoEstado } : item
        )
      );
    } catch (error) {
      console.error('Error al actualizar estado:', error);
      alert('Error al actualizar el estado');
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!user) return;
      
      try {
        setLoading(true);
        const response = await getBolsaDocumentos(user);
        
        let parsedData = response;
        if (response && response.body && typeof response.body === 'string') {
          parsedData = JSON.parse(response.body);
        }
        
        setData(parsedData || []);
        
        // Cargar estados para el select
        const estadosResponse = await getBolsaDocumentosSelect();
        let estadosData = estadosResponse;
        if (estadosResponse && estadosResponse.body && typeof estadosResponse.body === 'string') {
          estadosData = JSON.parse(estadosResponse.body);
        }
        setEstados(estadosData || []);
      } catch (err) {
        setError('Error al cargar los datos');
        console.error('Error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user]);

  const goToPage = (page) => {
    setCurrentPage(page);
  };

  const getPageNumbers = () => {
    const maxButtons = 7;
    if (totalPages <= maxButtons) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    
    const half = Math.floor(maxButtons / 2);
    let start = Math.max(1, currentPage - half);
    let end = Math.min(totalPages, start + maxButtons - 1);
    
    if (end - start + 1 < maxButtons) {
      start = Math.max(1, end - maxButtons + 1);
    }
    
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-baseline gap-4 mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Bolsa de documentos</h1>
        </div>
        <div className="flex justify-center items-center h-32">
          <AiOutlineLoading3Quarters className="animate-spin h-8 w-8" style={{color: 'var(--if-primary)'}} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="flex items-baseline gap-4 mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Bolsa de documentos</h1>
        </div>
        <div className="text-center py-8" style={{color: 'var(--if-text-muted)'}}>
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Bolsa de documentos</h1>
        {data.length > 0 && (
          <button
            onClick={exportToExcel}
            className="btn-primary"
            style={{
              background: 'linear-gradient(135deg, var(--if-success) 0%, #16a34a 100%)',
              padding: '0.5rem 1rem',
              fontSize: '0.875rem',
              borderLeft: '3px solid var(--if-primary)'
            }}
          >
            <MdReceipt className="w-4 h-4 mr-2" style={{display: 'inline'}} />
            Exportar Excel
          </button>
        )}
      </div>

      {data.length > 0 && (
        <div className="mb-4">
          <div className="relative" style={{ maxWidth: '500px' }}>
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <IoSearchOutline className="h-5 w-5" style={{ color: '#9ca3af' }} />
            </div>
            <input
              type="text"
              value={searchTerm}
              onChange={handleSearchChange}
              placeholder="Buscar en todas las columnas..."
              className="w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {searchTerm && (
              <button type="button" onClick={clearSearch} className="absolute inset-y-0 right-0 pr-3 flex items-center" style={{ color: '#9ca3af' }} title="Limpiar búsqueda">
                <IoClose className="h-5 w-5" />
              </button>
            )}
          </div>
          {searchTerm && (
            <div className="mt-2 text-sm" style={{ color: 'var(--if-text-muted)' }}>
              {sortedData.length === 0 ? <span>No se encontraron resultados para &quot;{searchTerm}&quot;</span> : <span>{sortedData.length} resultado{sortedData.length !== 1 ? 's' : ''} encontrado{sortedData.length !== 1 ? 's' : ''}{sortedData.length !== data.length ? ` de ${data.length} registros` : ''}</span>}
            </div>
          )}
        </div>
      )}
      
      <div className="seccion-datos">
        {data.length === 0 ? (
          <div className="text-center py-8" style={{color: 'var(--if-text-muted)'}}>
            No hay datos disponibles
          </div>
        ) : sortedData.length === 0 && searchTerm ? (
          <div className="text-center py-8" style={{color: 'var(--if-text-muted)'}}>No se encontraron resultados para &quot;{searchTerm}&quot;</div>
        ) : (
          <div className="tabla-container" style={{maxHeight: '500px', overflowY: 'auto', overflowX: 'auto'}}>
            <table style={{fontSize: '0.875rem', tableLayout: 'fixed', width: '100%'}}>
              <thead style={{position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 10}}>
                <tr>
                  <th style={{position: 'sticky', left: 0, backgroundColor: 'white', zIndex: 11, width: '100px', cursor: 'pointer', userSelect: 'none'}} onClick={() => handleSort('RUT_CLIENTE')}>RUT Cliente {getSortIcon('RUT_CLIENTE')}</th>
                  <th style={{position: 'sticky', left: '100px', backgroundColor: 'white', zIndex: 11, width: '250px', cursor: 'pointer', userSelect: 'none'}} onClick={() => handleSort('NOMBRE_CLIENTE')}>Cliente {getSortIcon('NOMBRE_CLIENTE')}</th>
                  <th style={{width: '200px', cursor: 'pointer', userSelect: 'none'}} onClick={() => handleSort('EJECUTIVO')}>Ejecutivo {getSortIcon('EJECUTIVO')}</th>
                  <th style={{width: '100px', cursor: 'pointer', userSelect: 'none'}} onClick={() => handleSort('RUT_DEUDOR')}>RUT Deudor {getSortIcon('RUT_DEUDOR')}</th>
                  <th style={{width: '250px', cursor: 'pointer', userSelect: 'none'}} onClick={() => handleSort('NOMBRE_DEUDOR')}>Deudor {getSortIcon('NOMBRE_DEUDOR')}</th>
                  <th style={{width: '110px', cursor: 'pointer', userSelect: 'none'}} onClick={() => handleSort('CLASIFICACION_DEUDOR')}>Clasificación Deudor {getSortIcon('CLASIFICACION_DEUDOR')}</th>
                  <th style={{width: '80px', cursor: 'pointer', userSelect: 'none'}} onClick={() => handleSort('TIPO_DOCTO')}>Tipo Doc {getSortIcon('TIPO_DOCTO')}</th>
                  <th style={{width: '80px', cursor: 'pointer', userSelect: 'none'}} onClick={() => handleSort('FOLIO')}>Folio {getSortIcon('FOLIO')}</th>
                  <th style={{width: '100px', cursor: 'pointer', userSelect: 'none'}} onClick={() => handleSort('FECHA_DOCTO')}>Fecha Doc {getSortIcon('FECHA_DOCTO')}</th>
                  <th style={{textAlign: 'right', width: '120px', cursor: 'pointer', userSelect: 'none'}} onClick={() => handleSort('MONTO_TOTAL')}>Monto Total {getSortIcon('MONTO_TOTAL')}</th>
                  <th style={{width: '100px', cursor: 'pointer', userSelect: 'none'}} onClick={() => handleSort('FECHA_RECLAMO')}>Fecha Reclamo {getSortIcon('FECHA_RECLAMO')}</th>
                  <th style={{textAlign: 'right', width: '120px', cursor: 'pointer', userSelect: 'none'}} onClick={() => handleSort('MONTO_NOTA_CREDITO')}>Monto Nota Crédito {getSortIcon('MONTO_NOTA_CREDITO')}</th>
                  <th style={{width: '240px', cursor: 'pointer', userSelect: 'none'}} onClick={() => handleSort('ESTADO')}>Estado {getSortIcon('ESTADO')}</th>
                </tr>
              </thead>
              <tbody>
                {currentData.map((item, index) => (
                  <tr key={index} style={{backgroundColor: item.ESTADO === 'Sin gestión' ? '#fee2e2' : 'transparent'}}>
                    <td style={{position: 'sticky', left: 0, backgroundColor: item.ESTADO === 'Sin gestión' ? '#fee2e2' : 'white', zIndex: 5}}>{item.RUT_CLIENTE}</td>
                    <td style={{position: 'sticky', left: '100px', backgroundColor: item.ESTADO === 'Sin gestión' ? '#fee2e2' : 'white', zIndex: 5}}>{item.NOMBRE_CLIENTE}</td>
                    <td>{item.EJECUTIVO}</td>
                    <td>{item.RUT_DEUDOR}</td>
                    <td>{item.NOMBRE_DEUDOR}</td>
                    <td>{item.CLASIFICACION_DEUDOR}</td>
                    <td>{item.TIPO_DOCTO}</td>
                    <td>{item.FOLIO}</td>
                    <td>{formatDate(item.FECHA_DOCTO)}</td>
                    <td style={{textAlign: 'right'}}>
                      ${item.MONTO_TOTAL?.toLocaleString('de-DE') || '0'}
                    </td>
                    <td>{formatDate(item.FECHA_RECLAMO)}</td>
                    <td style={{textAlign: 'right'}}>
                      {item.MONTO_NOTA_CREDITO ? `$${parseFloat(item.MONTO_NOTA_CREDITO).toLocaleString('de-DE')}` : '-'}
                    </td>
                    <td>
                      <select
                        value={item.ESTADO}
                        onChange={(e) => handleEstadoChange(item.ID, e.target.value)}
                        className="w-full px-2 py-1 text-xs border rounded"
                        style={{
                          backgroundColor: item.ESTADO === 'Sin gestión' ? '#fee2e2' : 'white',
                          color: item.ESTADO === 'Sin gestión' ? '#dc2626' : 'inherit'
                        }}
                      >
                        {estados.map((estado, idx) => (
                          <option key={idx} value={estado.ESTADO_DOCTO}>
                            {estado.ESTADO_DOCTO}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        
        {/* Paginación */}
        {sortedData.length > itemsPerPage && (
          <div className="flex justify-between items-center mt-4">
            <div className="text-sm" style={{color: 'var(--if-text-muted)'}}>
              Mostrando {startIndex + 1} a {Math.min(endIndex, sortedData.length)} de {sortedData.length} registros
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed"
                style={{borderColor: 'var(--if-gray-300)', color: 'var(--if-text-primary)'}}
              >
                Anterior
              </button>
              {getPageNumbers().map(page => (
                <button
                  key={page}
                  onClick={() => goToPage(page)}
                  className={`px-3 py-1 text-sm border rounded ${
                    currentPage === page ? 'bg-blue-500 text-white border-blue-500' : 'border-gray-300'
                  }`}
                >
                  {page}
                </button>
              ))}
              <button
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed"
                style={{borderColor: 'var(--if-gray-300)', color: 'var(--if-text-primary)'}}
              >
                Siguiente
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BolsaDocumentos;